import { GeminiChat } from './geminiChat';
import { buildEvaluationContext } from './context';
import { loadPromptSources } from './promptLoader';
import { scoringStep } from './steps/scoringStep';
import { qualificationStep } from './steps/qualificationStep';
import { analysisStep } from './steps/analysisStep';

const DEFAULT_STEPS = [scoringStep, qualificationStep, analysisStep];

export class EvaluationPipeline {
  constructor(steps = DEFAULT_STEPS) {
    this.steps = steps;
  }

  async run({ questionnaire, submission, onStepStart, onStepEnd }) {
    if (!questionnaire) {
      throw new Error('Fragebogen konnte nicht geladen werden');
    }

    const prompts = await loadPromptSources({ submission, questionnaire });
    const computed = buildEvaluationContext({ questionnaire, submission });
    if (computed.empty) {
      const err = new Error('Keine Fragen gefunden.');
      err.code = 'EMPTY';
      throw err;
    }

    const ctx = { questionnaire, submission, prompts, computed };
    const chat = new GeminiChat();
    const stepResults = {};

    for (const step of this.steps) {
      if (onStepStart) onStepStart(step.id);
      const startedAt = Date.now();
      const userText = step.buildPrompt(ctx, stepResults);
      const json = await chat.ask({ userText, schema: step.schema, model: step.model });
      stepResults[step.id] = step.parse(json, ctx);
      if (onStepEnd) onStepEnd(step.id, Date.now() - startedAt);
    }

    return this.assemble(stepResults, ctx);
  }

  assemble(results, ctx) {
    const { computed } = ctx;
    const processedScores = results.scoring?.processedScores || [];
    const perDefaultById = results.qualification?.perDefaultById || new Map();

    const enrichedScores = processedScores.map((s) => {
      const isDefault = String(s.questionId).startsWith('default_');
      if (!isDefault) {
        return { ...s, qualificationPassed: false };
      }
      const verdict = perDefaultById.get(String(s.questionId));
      const fallbackPassed = (Number(s.score) || 0) > 0;
      return {
        ...s,
        qualificationPassed: verdict?.passed ?? fallbackPassed,
        reason: s.reason || verdict?.reason || ''
      };
    });

    const analysisResult = results.analysis || {};
    const analysis = {
      score: analysisResult.analysisScore || 0,
      summary: analysisResult.summary || '',
      strengths: analysisResult.strengths || [],
      weaknesses: analysisResult.weaknesses || [],
      areasForDiscussion: analysisResult.areasForDiscussion || []
    };

    const defaultScores = enrichedScores.filter((s) => String(s.questionId).startsWith('default_'));
    const qualificationTotalScore = defaultScores.reduce(
      (sum, s) => sum + (Number(s.score) || 0),
      0
    );
    const qualification_evaluation = {
      score: qualificationTotalScore,
      explanation: '',
      questionEvaluations: defaultScores.map((s) => {
        const verdict = perDefaultById.get(String(s.questionId));
        return {
          questionId: s.questionId,
          score: Number(s.score) || 0,
          maxScore: Number(s.maxScore) || 0,
          passed: s.qualificationPassed ?? (Number(s.score) || 0) > 0,
          reason: s.reason || verdict?.reason || ''
        };
      })
    };

    const totalScoreResult = enrichedScores.reduce((sum, s) => sum + (Number(s.score) || 0), 0);

    // Per-page subtotals (ordered), so the results list/cards can show the
    // breakdown without re-deriving the questionnaire's page structure.
    const scoreById = new Map(enrichedScores.map((s) => [String(s.questionId), s]));
    const usedInPage = new Set();
    const pageScores = [];
    (computed.pageSections || []).forEach((section) => {
      const items = section.questionIds.map((id) => scoreById.get(String(id))).filter(Boolean);
      items.forEach((it) => usedInPage.add(String(it.questionId)));
      if (items.length === 0) return;
      pageScores.push({
        key: section.key,
        title: section.title,
        score: items.reduce((sum, it) => sum + (Number(it.score) || 0), 0),
        maxScore: items.reduce((sum, it) => sum + (Number(it.maxScore) || 0), 0)
      });
    });
    const leftoverScores = enrichedScores.filter((s) => !usedInPage.has(String(s.questionId)));
    if (leftoverScores.length) {
      pageScores.push({
        key: 'other',
        title: 'Weitere Fragen',
        score: leftoverScores.reduce((sum, it) => sum + (Number(it.score) || 0), 0),
        maxScore: leftoverScores.reduce((sum, it) => sum + (Number(it.maxScore) || 0), 0)
      });
    }

    const point_evaluation = {
      totalScore: Math.min(totalScoreResult, computed.totalScoreBudget),
      maxScore: computed.totalScoreBudget,
      scoredByAI: true,
      questionScores: enrichedScores,
      pageScores,
      manualOverrides: {},
      summary: analysis.summary,
      strengths: analysis.strengths,
      weaknesses: analysis.weaknesses
    };

    return { analysis, qualification_evaluation, point_evaluation };
  }
}

export const evaluationPipeline = new EvaluationPipeline();
